import type { FinanceData, FinanceTransaction, SuggestedAction } from '../types'

export const money = (value: number) => new Intl.NumberFormat('fa-IR').format(Math.round(value)) + ' تومان'
export const compactMoney = (value: number) => `${new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 }).format(value / 1_000_000)} مـ`
export const toEnglishDigits = (value: string) => value
  .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
  .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))

export function monthSummary(data: FinanceData, monthKey: string) {
  const txs = data.transactions.filter((item) => item.monthKey === monthKey)
  const income = txs.filter((item) => item.type === 'Income').reduce((sum, item) => sum + item.amount, 0)
  const expense = txs.filter((item) => item.type === 'Expense').reduce((sum, item) => sum + item.amount, 0)
  const installments = data.installments.filter((item) => item.monthKey === monthKey && item.status !== 'Paid')
    .reduce((sum, item) => sum + item.amount, 0)
  return { income, expense, installments, freeCash: income - expense - installments }
}

export function deterministicInsights(data: FinanceData, monthKey: string) {
  const s = monthSummary(data, monthKey)
  const burden = s.income ? Math.round((s.installments / s.income) * 100) : 0
  const next = Object.keys([...data.transactions.map((x) => x.monthKey), ...data.installments.map((x) => x.monthKey)]
    .reduce<Record<string, true>>((acc, key) => ({ ...acc, [key]: true }), {}))
    .sort().filter((key) => key >= monthKey).slice(0, 7)
    .map((key) => ({ key, ...monthSummary(data, key) }))
  const best = [...next].sort((a, b) => b.freeCash - a.freeCash)[0]
  const warnings: string[] = []
  if (burden >= 60) warnings.push(`نسبت اقساط به درآمد ${burden}٪ است؛ فشار نقدینگی این ماه بالاست.`)
  if (s.freeCash < 0) warnings.push(`کسری این ماه ${money(Math.abs(s.freeCash))} است.`)
  if (best && best.key !== monthKey) warnings.push(`بیشترین فضای آزاد نزدیک در ${best.key} با ${money(best.freeCash)} است.`)
  if (!warnings.length) warnings.push('تعهدهای ثبت‌شده با درآمد ماه هماهنگ‌اند؛ هزینه‌های روزانه را هم ثبت کن تا تحلیل دقیق‌تر شود.')
  return { burden, warnings, forecast: next }
}

const categoryAliases: Record<string, string[]> = {
  غذا: ['غذا', 'رستوران', 'ناهار', 'شام', 'صبحانه'],
  سوخت: ['بنزین', 'سوخت'],
  خرید: ['خرید', 'فروشگاه'],
  درمان: ['دکتر', 'درمان', 'دارو', 'پزشک'],
  قبض: ['قبض', 'آب', 'برق', 'گاز', 'اینترنت'],
  حقوق: ['حقوق', 'دستمزد'],
}

export function parsePersianFinanceText(text: string): SuggestedAction | null {
  const normalized = toEnglishDigits(text).replace(/[٬,]/g, '').trim()
  const amountMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(میلیون|هزار)?/)
  if (!amountMatch) return null
  let amount = Number(amountMatch[1])
  if (amountMatch[2] === 'میلیون') amount *= 1_000_000
  if (amountMatch[2] === 'هزار') amount *= 1_000
  if (!amount || amount < 100) return null
  const isIncome = /(درآمد|حقوق|واریز|گرفتم|فروختم)/.test(normalized)
  const categoryName = Object.entries(categoryAliases).find(([, aliases]) => aliases.some((alias) => normalized.includes(alias)))?.[0]
    ?? (isIncome ? 'سایر درآمدها' : 'سایر')
  const date = normalized.match(/(1[34]\d{2})[/-](\d{1,2})[/-](\d{1,2})/)
  const now = new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date()).replace(/\u200e/g, '')
  const dateJalali = date ? `${date[1]}/${date[2].padStart(2, '0')}/${date[3].padStart(2, '0')}` : toEnglishDigits(now)
  return {
    kind: 'transaction', type: isIncome ? 'Income' : 'Expense', amount,
    categoryName, description: text.trim(), dateJalali, confidence: categoryName === 'سایر' ? .72 : .9,
  }
}

export function actionToTransaction(action: SuggestedAction, data: FinanceData): FinanceTransaction {
  const category = data.categories.find((item) => item.name === action.categoryName && item.type === action.type)
    ?? data.categories.find((item) => item.type === action.type)
  const account = data.accounts[0]
  return {
    id: crypto.randomUUID(), type: action.type, amount: action.amount,
    dateJalali: action.dateJalali, monthKey: action.dateJalali.slice(0, 7).replace('/', '-'),
    categoryId: category?.id ?? 'unknown', categoryName: category?.name ?? action.categoryName,
    accountId: account?.id ?? 'unknown', accountTitle: account?.title ?? 'بدون حساب',
    description: action.description, postingStatus: 'Posted', createdAt: new Date().toISOString(),
  }
}
